import {useCallback, useMemo} from 'react';
import {useParams} from 'react-router-dom';
import {RunWithRunsetInfo} from 'src/containers/RunsDataLoader';
import {RunSetPart} from 'src/state/views/runSet/types';

import * as CustomRunColorsActions from '../../../state/views/customRunColors/actions';
import * as ViewHooks from '../../../state/views/hooks';
import {usePart} from '../../../state/views/hooks';
import {runColor} from '../../../util/colors';
import * as QueryTypes from '../../../util/queryTypes';
import {Sort} from '../../../util/queryTypes';
import {useRampFlagResetRunColors} from '../../../util/rampFeatureFlags';
import {uniqueId} from '../../../util/runs';
import {usePanelsSharedRefsContext} from '../../Views/contexts/PanelsSharedRefsContext';
import {activeFilterCount} from './WBTableActionsFilter';

export type ResetColorsConfig = {
  hasCustomRunColors: boolean;
  hasActiveFilter: boolean;
  hasActiveSearch: boolean;
  modifiedSort: boolean;
  randomizeColors: () => void;
  resetColors: () => void;
  useResetRunColors: boolean;
};

export const useGroupKeysByRunsetId = (runSets: RunSetPart[]) => {
  const groupSelections = ViewHooks.useParts(
    runSets.map(runSet => runSet.groupSelectionsRef)
  );

  const groupKeysByRunSetId = useMemo(() => {
    return runSets.reduce((acc, runSet, index) => {
      acc[runSet.id] = groupSelections[index].grouping;
      return acc;
    }, {} as Record<string, QueryTypes.Grouping>);
  }, [runSets, groupSelections]);

  const getGroupKeysByRunsetId = useCallback(
    (runsetId: string) => {
      return groupKeysByRunSetId[runsetId];
    },
    [groupKeysByRunSetId]
  );

  return getGroupKeysByRunsetId;
};

export const useDetectActiveSearch = (runSets: RunSetPart[]) => {
  const getActiveSearchByRunsetId = useCallback(
    (runsetId: string) => {
      return runSets.find(runSet => runSet.id === runsetId)?.search.query;
    },
    [runSets]
  );

  return getActiveSearchByRunsetId;
};

export const useDetectActiveFilters = (runSets: RunSetPart[]) => {
  const filters = ViewHooks.useParts(runSets.map(runSet => runSet.filtersRef));

  const activeFiltersByRunsetId = useMemo(() => {
    return runSets.reduce((acc, runSet, index) => {
      acc[runSet.id] = activeFilterCount(filters[index]) > 0;
      return acc;
    }, {} as Record<string, boolean>);
  }, [runSets, filters]);

  const getActiveFiltersByRunsetId = useCallback(
    (runsetId: string) => {
      return activeFiltersByRunsetId[runsetId];
    },
    [activeFiltersByRunsetId]
  );

  const hasActiveFilter = useMemo(() => {
    return runSets.some(runSet => getActiveFiltersByRunsetId(runSet.id));
  }, [runSets, getActiveFiltersByRunsetId]);

  return hasActiveFilter;
};

/**
 * The way this works is that we're hijacking the custom run colors functionality to randomize the colors of runs after a sort. Runs are typically colored by their `defaultColorIndex` property, and we can easily override this by selectively overriding that value when calculating the color. We then store that color in the custom run colors slot in the view spec, so that it persists across user interactions and can easily be copied into reports.
 *
 * Resetting is a simple matter of nulling out those values in the custom run colors slot. This won't null out all custom run colors— only run colors that are in view through the selector (exposed to this hook as `rows`).
 *
 * Grouped runs work the same way, only instead of keying them by name in the customRunColors slot we construct the unique ID using the group keys.
 */
export function useResetColors({
  sort,
  rows,
}: {
  sort: Sort;
  rows: RunWithRunsetInfo[];
}) {
  const {entityName} = useParams<{entityName: string}>();
  const useResetRunColors = useRampFlagResetRunColors(entityName);
  const {customRunColorsRef, runSetRefs} = usePanelsSharedRefsContext();
  const runSets = ViewHooks.useParts(runSetRefs);
  const hasActiveFilter = useDetectActiveFilters(runSets);
  const getActiveSearchByRunsetId = useDetectActiveSearch(runSets);
  const hasActiveSearch = useMemo(() => {
    return rows.some(r => getActiveSearchByRunsetId(r.runsetInfo.id));
  }, [getActiveSearchByRunsetId, rows]);

  const getGroupKeysByRunsetId = useGroupKeysByRunsetId(runSets);
  const setCustomRunColor = ViewHooks.useViewAction(
    customRunColorsRef,
    CustomRunColorsActions.setCustomRunColor
  );
  const customRunColors = usePart(customRunColorsRef);

  const rowIds = rows.map(r =>
    uniqueId(r, getGroupKeysByRunsetId(r.runsetInfo.id))
  );
  const hasActiveGroupKeys = rows.some(
    r => getGroupKeysByRunsetId(r.runsetInfo.id).length > 0
  );
  const hasCustomRunColors = rowIds.some(r => r in customRunColors);

  const randomizeColors = useCallback(() => {
    rows.forEach((r, i) => {
      const color = runColor(
        {...r, defaultColorIndex: i},
        getGroupKeysByRunsetId(r.runsetInfo.id),
        {},
        undefined,
        undefined,
        true
      );
      setCustomRunColor(
        uniqueId(r, getGroupKeysByRunsetId(r.runsetInfo.id)),
        color
      );
    });
  }, [getGroupKeysByRunsetId, rows, setCustomRunColor]);

  const resetColors = useCallback(() => {
    rows.forEach(r => {
      const uniq = uniqueId(r, getGroupKeysByRunsetId(r.runsetInfo.id));
      if (uniq in customRunColors) {
        setCustomRunColor(uniq, null);
      }
    });
  }, [getGroupKeysByRunsetId, rows, setCustomRunColor, customRunColors]);

  return useMemo(() => {
    return {
      hasActiveGroupKeys,
      hasActiveFilter,
      hasActiveSearch,
      hasCustomRunColors,
      modifiedSort:
        sort.keys.length !== 1 || sort.keys[0].key.name !== 'createdAt',
      randomizeColors,
      resetColors,
      useResetRunColors,
    };
  }, [
    hasActiveGroupKeys,
    hasActiveFilter,
    hasActiveSearch,
    hasCustomRunColors,
    randomizeColors,
    resetColors,
    sort,
    useResetRunColors,
  ]);
}
